/**
 * Authentication hook for managing user authentication state
 */

import { useState, useEffect } from 'react';
import { authService } from '@/services/auth-service';

export interface AuthUser {
  email: string;
  name?: string;
  role?: string;
  sub: string;
}

export interface UseAuthResult {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
}

/**
 * Parse user information from JWT token
 */
function parseUserFromToken(idToken: string): AuthUser | null {
  try {
    const parts = idToken.split('.');
    if (parts.length < 2) return null;
    
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    
    return {
      email: payload.email || '',
      name: payload.name || payload.given_name || payload.email?.split('@')[0] || '',
      role: payload['custom:role'] || payload.role || 'user',
      sub: payload.sub || ''
    };
  } catch (error) {
    console.error('Failed to parse user from token:', error);
    return null;
  }
}

/**
 * Custom hook for authentication management
 */
export function useAuth(): UseAuthResult {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadUser() {
      try {
        const idToken = await authService.getIdToken();
        if (idToken && mounted) {
          const userData = parseUserFromToken(idToken);
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to load user:', error);
        if (mounted) {
          setUser(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadUser();

    return () => {
      mounted = false;
    };
  }, []);

  const logout = async (): Promise<void> => {
    try {
      setUser(null);
      await authService.logoutAndRedirect();
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback to local logout and redirect to login
      authService.logout();
      setUser(null);
      window.location.assign('/login');
    }
  };

  return {
    user,
    isAuthenticated: !!user && authService.isAuthenticated(),
    isLoading,
    logout
  };
}
